import {
  alpha,
  Box,
  Button,
  Divider,
  IconButton,
  Link,
  ListItem,
  ListItemAvatar,
  ListItemText,
  makeStyles,
  Typography,
} from '@material-ui/core';
import InsertDriveFileIcon from '@material-ui/icons/InsertDriveFile';
import LaunchOutlinedIcon from '@material-ui/icons/LaunchOutlined';
import PlayArrowIcon from '@material-ui/icons/PlayArrow';
import ReactPlayer, { ReactPlayerProps } from 'react-player';

import { useSearchQuery } from 'contexts/SearchQueryContext';
import {
  MensagemCtaInterface,
  TiposDeCabecalhoMensagemInterativaEnum,
} from 'store/reducers/mensagens';
import { getFileNameAndExtension } from 'utils/getFileNameAndExtension';
import parseTextToHtml from 'utils/parseTextToHtml';

import ImageViewer from './ImageViewer';
import MessageBase, { MessageBaseProps } from './MessageBase';
import MessageErrorFallback from './MessageErrorFallback';
import ReadMoreParsedText from './ReadMoreParsedText';

type CtaUrlButtonMessageProps = MessageBaseProps;

const useStyles = makeStyles(({ spacing, palette }) => ({
  root: {
    width: '100%',
    position: 'relative',
  },
  imageContainer: {
    minWidth: spacing(40),
    height: spacing(18),
    overflow: 'hidden',
    position: 'relative',
    borderRadius: spacing(1),
  },
  divider: {
    backgroundColor: palette.text.secondary,
    marginLeft: -16,
    marginRight: -16,
  },
  button: {
    color: 'inherit',
  },
  buttonIcon: {
    color: 'inherit',
  },
  document: {
    display: 'flex',
    alignItems: 'center',
    overflow: 'hidden',
    position: 'relative',
    minWidth: spacing(40),
    paddingBottom: spacing(1),
  },
  listItem: {
    color: 'inherit',
    borderRadius: spacing(1),
  },
  titulo: {
    overflow: 'hidden',
    width: spacing(24),
  },
  iconButton: {
    color: palette.getContrastText(alpha(palette.common.black, 0.4)),
    backgroundColor: `${alpha(palette.common.black, 0.4)} !important`,
  },
  messageText: {
    display: 'flex',
    flexDirection: 'column',
    gap: spacing(1),
    wordBreak: 'break-word',
    whiteSpace: 'normal',
  },
  readMoreButton: {
    paddingBlock: 0,
    minWidth: 'fit-content',
    textTransform: 'none',
    alignSelf: 'flex-start',
    color: palette.text.secondary,
  },
  headerText: {
    fontWeight: 600,
    wordBreak: 'break-word',
    whiteSpace: 'normal',
  },
  footerText: {
    wordBreak: 'break-word',
    whiteSpace: 'normal',
  },
}));

function CtaUrlButtonMessage({ ...props }: CtaUrlButtonMessageProps) {
  const classes = useStyles();
  const searchQuery = useSearchQuery();

  try {
    const {
      mensagem: { whatsappInteractiveComponents },
    } = props;

    const { header, body, footer, action } =
      whatsappInteractiveComponents as MensagemCtaInterface;

    const renderHeader = (type: TiposDeCabecalhoMensagemInterativaEnum) => {
      switch (type) {
        case TiposDeCabecalhoMensagemInterativaEnum.TEXTO:
          return (
            <Typography
              variant="subtitle2"
              style={{ fontWeight: 'bold' }}
              dangerouslySetInnerHTML={{
                __html: parseTextToHtml(header?.text || '', searchQuery),
              }}
            />
          );
        case TiposDeCabecalhoMensagemInterativaEnum.IMAGEM:
          return (
            <Box className={classes.imageContainer}>
              <ImageViewer src={header?.image?.link}>
                <img
                  src={header?.image?.link}
                  alt=""
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    objectPosition: 'center',
                  }}
                />
              </ImageViewer>
            </Box>
          );
        case TiposDeCabecalhoMensagemInterativaEnum.DOCUMENTO: {
          if (!header?.document) return null;
          const { name, extension } = getFileNameAndExtension(
            header.document.link,
          );
          return (
            <Box className={classes.document}>
              <ListItem
                className={classes.listItem}
                title="Baixar documento"
                component={Link}
                target="_blank"
                rel="noopener noreferrer"
                href={header.document.link}
                disableGutters
              >
                <ListItemAvatar>
                  <InsertDriveFileIcon fontSize="large" color="inherit" />
                </ListItemAvatar>
                <Typography variant="body2" className={classes.titulo}>
                  {`${name}${extension ? `.${extension}` : ''}`}
                </Typography>
              </ListItem>
            </Box>
          );
        }
        case TiposDeCabecalhoMensagemInterativaEnum.VIDEO: {
          const playerOptions = {
            url: header?.video?.link || '',
            pip: true,
            controls: true,
            width: '100%',
            height: '100%',
            playIcon: (
              <IconButton className={classes.iconButton}>
                <PlayArrowIcon color="inherit" fontSize="large" />
              </IconButton>
            ),
          } as ReactPlayerProps;
          return <ReactPlayer {...playerOptions} />;
        }
        default:
          return null;
      }
    };

    return (
      <MessageBase {...props}>
        <Box className={classes.root}>
          {header && header.type && <Box>{renderHeader(header.type)}</Box>}

          <ListItemText
            className={classes.messageText}
            primary={
              <ReadMoreParsedText texto={body.text || ''} variant="body2" />
            }
            secondary={
              footer?.text ? (
                <Typography
                  variant="caption"
                  color="textSecondary"
                  dangerouslySetInnerHTML={{
                    __html: parseTextToHtml(footer.text, searchQuery),
                  }}
                />
              ) : null
            }
          />

          <Box my={1}>
            <Divider className={classes.divider} />
          </Box>

          <Button
            className={classes.button}
            variant="text"
            startIcon={<LaunchOutlinedIcon className={classes.buttonIcon} />}
            size="small"
            fullWidth
            onClick={() => {
              window.open(action.parameters.url, '_blank');
            }}
          >
            {action.parameters.display_text}
          </Button>
        </Box>
      </MessageBase>
    );
  } catch (error) {
    return <MessageErrorFallback {...props} />;
  }
}

export default CtaUrlButtonMessage;
